/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...

/* H3 product-card photos: native touch scrolling, keyboard/arrows, and mouse dragging. */
(() => {
  if (customElements.get('al-card-gallery')) return;
  class ALCardGallery extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.imageObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.track = this.querySelector('.al-card-gallery__track');
      this.slides = [...this.querySelectorAll('.al-card-gallery__slide')];
      this.prev = this.querySelector('[data-photo-prev]');
      this.next = this.querySelector('[data-photo-next]');
      this.counter = this.querySelector('.al-card-gallery__count');
      if (!this.track || this.slides.length < 2) return;
      this.index = 0;
      this.imagesActive = !this.hasAttribute('data-al-deferred-gallery');
      this.trackWidth = 0;
      this.renderedIndex = -1;
      this.suppressClick = false;
      this.prev.addEventListener('click', () => this.go(this.index - 1), {signal});
      this.next.addEventListener('click', () => this.go(this.index + 1), {signal});
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, {signal, passive: true});
      this.track.addEventListener('keydown', event => {
        const destinations = {ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1};
        if (!(event.key in destinations)) return;
        event.preventDefault();
        event.stopPropagation();
        this.go(destinations[event.key]);
      }, {signal});
      this.track.addEventListener('dragstart', event => event.preventDefault(), {signal});
      this.track.addEventListener('pointerdown', event => {
        this.activateImages();
        if (!event.isPrimary || event.button !== 0) return;
        this.suppressClick = false;
        this.pointer = {id:event.pointerId, type:event.pointerType, x:event.clientX, y:event.clientY, left:this.track.scrollLeft, index:this.index, moved:false};
      }, {signal, passive:true});
      this.track.addEventListener('pointermove', event => {
        const p = this.pointer;
        if (!p || p.id !== event.pointerId) return;
        const dx = event.clientX - p.x, dy = event.clientY - p.y;
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) p.moved = true;
        if (p.type !== 'mouse' || !p.moved) return;
        event.preventDefault();
        this.track.setPointerCapture(event.pointerId);
        this.track.setAttribute('data-dragging', '');
        this.track.scrollLeft = p.left - dx;
        this.suppressClick = true;
      }, {signal});
      const finish = event => {
        const p = this.pointer;
        if (!p || p.id !== event.pointerId) return;
        this.suppressClick = p.moved || event.type === 'pointercancel';
        if (p.type === 'mouse' && p.moved) {
          const dx = event.clientX - p.x;
          this.track.removeAttribute('data-dragging');
          this.go(p.index + (Math.abs(dx) > 25 ? (dx < 0 ? 1 : -1) : 0));
        }
        this.pointer = null;
      };
      this.track.addEventListener('pointerup', finish, {signal});
      this.track.addEventListener('pointercancel', finish, {signal});
      this.track.addEventListener('click', event => {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        }
      }, {signal, capture:true});
      this.resize = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (!width || width === this.trackWidth) return;
        this.trackWidth = width;
        if (this.index !== 0) this.track.scrollTo({left:this.index * width, behavior:'instant'});
        this.update();
      });
      this.resize.observe(this.track);
      this.setupImageLoading();
      this.setAttribute('data-ready', '');
      this.update();
    }
    setupImageLoading() {
      if (!this.hasAttribute('data-al-deferred-gallery')) return;
      if (!('IntersectionObserver' in window)) {
        this.imagesActive = true;
        this.querySelectorAll('img[data-al-src]').forEach(image => this.loadImage(image));
        return;
      }
      this.imageObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.activateImages();
      }, {rootMargin:'200px 0px', threshold:0});
      this.imageObserver.observe(this);
    }
    loadImage(image) {
      if (!image?.hasAttribute('data-al-src')) return;
      image.loading = 'eager';
      const srcset = image.getAttribute('data-al-srcset');
      if (srcset) image.setAttribute('srcset', srcset);
      image.setAttribute('src', image.getAttribute('data-al-src'));
      image.removeAttribute('data-al-srcset');
      image.removeAttribute('data-al-src');
    }
    activateImages() {
      this.imagesActive = true;
      this.imageObserver?.disconnect();
      this.loadNearbyImages(this.index);
    }
    loadNearbyImages(index) {
      if (!this.imagesActive) return;
      for (let offset = -1; offset <= 1; offset++) {
        const image = this.slides[index + offset]?.querySelector('img[data-al-src]');
        this.loadImage(image);
      }
    }
    update() {
      if (this.trackWidth) this.index = Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / this.trackWidth)));
      this.loadNearbyImages(this.index);
      if (this.renderedIndex === this.index) return;
      this.renderedIndex = this.index;
      this.prev.disabled = this.index === 0;
      this.next.disabled = this.index === this.slides.length - 1;
      this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.counter.setAttribute('aria-label', `Photo ${this.index + 1} of ${this.slides.length}`);
      this.slides.forEach((slide,index) => { slide.tabIndex = index === this.index ? 0 : -1; });
    }
    go(index) {
      const next = Math.min(this.slides.length - 1, Math.max(0, index));
      this.activateImages();
      this.loadNearbyImages(next);
      this.track.scrollTo({left:next * (this.trackWidth || this.track.clientWidth), behavior:'instant'});
      this.update();
    }
    disconnectedCallback() {
      this.imageObserver?.disconnect();
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }
  customElements.define('al-card-gallery', ALCardGallery);
})();
